/**
 * The operator screen's keymap as show-style data (D32: what an operator
 * does belongs to the show style): which keys run which OPERATOR ACTION.
 * The engine never reads it — it is the SPA's — but it lives with the show
 * style's schema, so a show style carries its keymap into the production's
 * snapshot (D22) like its adlib panels and their item hotkeys.
 *
 *   keymap: { take: ['F12', 'NumpadEnter'], 'set-next': ['N'] }
 *
 * An action the show style does not name keeps the built-in keys
 * (`apps/web/src/lib/playout/keymap.ts`); an empty list unbinds it. A user's
 * own layer (the SPA's keymap editor) goes on top of this one.
 *
 * A combo is `[Modifier+]…Key`: modifiers `Shift`, `Ctrl`, `Alt`, `Meta`
 * (and the aliases `Control`, `Option`, `Cmd`), the key a `KeyboardEvent.key`
 * name (`F1`, `ArrowDown`, `N`, `Space`, `Enter`) or `NumpadEnter`.
 */
import { z } from 'zod';

/** What an operator's key can do on the playout screen (the adlib items' hotkeys are the panels'). */
export const OPERATOR_ACTIONS = ['take', 'next-forward', 'next-back', 'select-down', 'select-up', 'set-next', 'set-next-segment', 'queue-segment'] as const;
export type OperatorAction = (typeof OPERATOR_ACTIONS)[number];

export const operatorActionSchema = z.enum(OPERATOR_ACTIONS);

/** A key combination as written (`Shift+F2`): up to four modifiers, then one key name without spaces or `+`. */
export const hotkeyComboSchema = z
  .string()
  .max(64)
  .regex(/^(?:(?:Shift|Ctrl|Control|Alt|Option|Meta|Cmd)\+){0,4}[^\s+]{1,32}$/i, { message: 'not a key combination like F12, Shift+N or Ctrl+Alt+K' });

/** At most this many keys per action (an action with more is a keymap nobody can read). */
export const MAX_KEYS_PER_ACTION = 4;

export const keymapSchema = z.partialRecord(operatorActionSchema, z.array(hotkeyComboSchema).max(MAX_KEYS_PER_ACTION));
export type Keymap = z.output<typeof keymapSchema>;
